import { ErrorType, ApiError, ValidationError } from '../types/errors';

export class ApiRequestError extends Error implements ApiError {
  type: ErrorType;
  originalError?: any;
  statusCode?: number;
  retryable: boolean;
  validationErrors?: ValidationError[];

  constructor(error: ApiError) {
    super(error.message);
    this.name = 'ApiRequestError';
    this.type = error.type;
    this.originalError = error.originalError;
    this.statusCode = error.statusCode;
    this.retryable = error.retryable;
    this.validationErrors = error.validationErrors;
  }
}

export class ErrorHandler {
  private static getResponseData(error: any): any {
    return error?.response?.data ?? error?.originalError?.response?.data ?? null;
  }

  static isApiError(error: any): error is ApiError {
    return Boolean(error && typeof error === 'object' && 'type' in error && 'message' in error);
  }

  static isUnauthorizedError(error: any): boolean {
    return (
      error?.statusCode === 401 ||
      error?.response?.status === 401 ||
      error?.originalError?.statusCode === 401 ||
      error?.originalError?.response?.status === 401 ||
      error?.type === ErrorType.AUTHENTICATION ||
      error?.originalError?.type === ErrorType.AUTHENTICATION
    );
  }

  static classifyError(error: any): ApiError {
    if (this.isApiError(error)) {
      return error;
    }

    if (!error.response && !error.request) {
      // Network or configuration error
      return {
        type: ErrorType.NETWORK,
        message: 'Network connection error. Please check your internet connection.',
        originalError: error,
        retryable: true
      };
    }

    if (error.response) {
      const status = error.response.status;
      const data = error.response.data;

      switch (status) {
        case 401:
          return {
            type: ErrorType.AUTHENTICATION,
            message: 'Please log in to continue.',
            statusCode: status,
            originalError: error,
            retryable: false
          };

        case 403:
          return {
            type: ErrorType.AUTHORIZATION,
            message: 'You don\'t have permission to perform this action.',
            statusCode: status,
            originalError: error,
            retryable: false
          };

        case 400:
          const validationErrors = this.extractValidationErrors(error);
          return {
            type: ErrorType.VALIDATION,
            message: data?.detail || validationErrors[0]?.message || 'Invalid data provided.',
            validationErrors,
            statusCode: status,
            originalError: error,
            retryable: false
          };

        case 429:
          return {
            type: ErrorType.RATE_LIMIT,
            message: 'Too many requests. Please wait and try again.',
            statusCode: status,
            originalError: error,
            retryable: true
          };

        case 500:
        case 502:
        case 503:
        case 504:
          return {
            type: ErrorType.SERVER,
            message: 'Server error. Please try again later.',
            statusCode: status,
            originalError: error,
            retryable: true
          };

        default:
          return {
            type: ErrorType.UNKNOWN,
            message: data?.detail || 'An unexpected error occurred.',
            statusCode: status,
            originalError: error,
            retryable: false
          };
      }
    }

    // Request made but no response received
    return {
      type: ErrorType.NETWORK,
      message: 'Network error. Please try again.',
      originalError: error,
      retryable: true
    };
  }

  static extractValidationErrors(error: any): ValidationError[] {
    const data = this.getResponseData(error);
    if (!data || typeof data !== 'object') return [];

    const errors: ValidationError[] = [];

    Object.entries(data).forEach(([field, rawValue]) => {
      if (field === 'detail') return;
      const values = Array.isArray(rawValue) ? rawValue : [rawValue];
      values.forEach((value) => {
        if (value === null || value === undefined) return;
        errors.push({
          field,
          message: typeof value === 'string' ? value : String(value),
        });
      });
    });

    return errors;
  }

  static getRetryDelay(attempt: number, maxDelay = 10000): number {
    // Exponential backoff with jitter
    const baseDelay = Math.min(1000 * Math.pow(2, attempt), maxDelay);
    const jitter = Math.random() * 0.3 * baseDelay;
    return baseDelay + jitter;
  }

  static async retryRequest<T>(
    requestFn: () => Promise<T>,
    maxRetries = 3,
    shouldRetry?: (error: ApiError) => boolean
  ): Promise<T> {
    let lastError: ApiError;

    for (let attempt = 0; attempt <= maxRetries; attempt++) {
      try {
        return await requestFn();
      } catch (error) {
        lastError = this.classifyError(error);
        
        // Don't retry if it's the last attempt or error is not retryable
        if (attempt === maxRetries || !lastError.retryable) {
          if (shouldRetry && !shouldRetry(lastError)) {
            break;
          }
          throw lastError;
        }

        // Wait before retrying
        const delay = this.getRetryDelay(attempt);
        await new Promise(resolve => setTimeout(resolve, delay));
      }
    }

    throw new ApiRequestError(lastError!);
  }
}

// Global error handler for unhandled promise rejections
export const setupGlobalErrorHandler = () => {
  window.addEventListener('unhandledrejection', (event) => {
    console.error('Unhandled promise rejection:', event.reason);
    const apiError = ErrorHandler.classifyError(event.reason);
    console.error('Classified error:', apiError);
  });
};
